import { getDistance } from '../utils/math.js';
import { audioManager } from '../systems/AudioManager.js';
import { isDebugMode } from '../utils/debug.js';
import { GAME_CONFIG, AI_CONFIG, RENDER_CONFIG } from '../config.js';

// Base class for all unit types (soldiers, medics, gatherers, etc.)
export class Unit {
  constructor(x, y, canvas, team, base = null) {
    // Position and movement
    this.x = x;
    this.y = y;
    this.vx = 0;
    this.vy = 0;
    this.speed = 0.75; // Base speed, can be overridden
    
    // Identity
    this.id = Unit.nextId++;
    this.team = team;
    this.color = team.color;
    this.base = base;
    
    // World reference
    this.canvas = canvas;
    
    // Status
    this.alive = true;
    this.health = 100;
    this.maxHealth = 100;
    this.size = 4;
    
    // Visual effects
    this.damageIndicator = 0;
    this.healingGlow = 0;
    
    // State management
    this.state = 'idle';
    this.previousState = null;
    
    // AI reference (will be set by subclasses)
    this.ai = null;

    // Unit systems (radio, engine, etc.)
    this.systems = new Map();

    // Performance optimization
    this.lastUpdate = Date.now();
  }

  // Core update method - called every frame
  update(delta, units, projectiles, resources, bases) {
    if (!this.alive) return;

    const now = Date.now();
    const timeDelta = now - this.lastUpdate;
    this.lastUpdate = now;

    // Update visual effects
    this.updateEffects(timeDelta);

    // Update unit systems before AI so they can influence behavior
    this.updateSystems(delta, units, projectiles, resources, bases);

    // Let AI make decisions (if we have one)
    if (this.ai) {
      this.ai.update(delta, units, projectiles, resources, bases);
    }
    
    // Update movement
    this.updateMovement(delta);
    
    // Check health
    if (this.health <= 0) {
      this.die();
    }
  }

  // Register a system (radio, engine, etc.) with this unit
  registerSystem(name, system) {
    if (!name || !system) return;
    this.systems.set(name, system);
    if (typeof system.initialize === 'function') {
      system.initialize(this);
    }
    return system;
  }

  // Retrieve a previously registered system
  getSystem(name) {
    return this.systems.get(name);
  }

  // Update all registered systems
  updateSystems(delta, units, projectiles, resources, bases) {
    if (!this.systems || this.systems.size === 0) return;

    const context = {
      delta,
      units,
      projectiles,
      resources,
      bases
    };

    for (const system of this.systems.values()) {
      if (system && typeof system.update === 'function') {
        system.update(delta, context);
      }
    }
  }
  
  // Update visual effects
  updateEffects(delta) {
    // Fade damage indicator
    if (this.damageIndicator > 0) {
      this.damageIndicator = Math.max(0, this.damageIndicator - delta * 0.001);
    }
    
    // Fade healing glow
    if (this.healingGlow > 0) {
      this.healingGlow = Math.max(0, this.healingGlow - delta * 0.001);
    }
  }
  
  // Update position based on velocity
  updateMovement(delta) {
    // Update position
    this.x += this.vx;
    this.y += this.vy;
    
    // Apply friction
    this.vx *= 0.9;
    this.vy *= 0.9;
    
    // Handle edge wrapping
    this.handleEdgeWrapping();
  }
  
  // Wrap around screen edges
  handleEdgeWrapping() {
    if (this.x < 0) this.x = this.canvas.width;
    if (this.x > this.canvas.width) this.x = 0;
    if (this.y < 0) this.y = this.canvas.height;
    if (this.y > this.canvas.height) this.y = 0;
  }
  
  // Take damage from an attack
  takeDamage(damage, attacker) {
    if (!this.alive) return false;
    
    this.health -= damage;
    this.damageIndicator = 1;
    audioManager.playHit();
    
    // Check if unit died
    if (this.health <= 0) {
      this.health = 0;
      if (attacker && attacker.onKill) {
        attacker.onKill(this);
      }
      return true; // Unit died
    }
    
    return false; // Unit survived
  }
  
  // Heal the unit
  heal(amount) {
    if (!this.alive) return;
    
    const previousHealth = this.health;
    this.health = Math.min(this.maxHealth, this.health + amount);
    const actualHealed = this.health - previousHealth;
    
    if (actualHealed > 0) {
      this.healingGlow = 1;
    }
    
    return actualHealed;
  }
  
  // Handle unit death
  die() {
    this.alive = false;
    this.health = 0;
    audioManager.playKill();
    
    // Subclasses can override to add specific death behavior
    this.onDeath();
  }
  
  // Override in subclasses for specific death behavior
  onDeath() {
    // Base units just die, subclasses might drop resources, explode, etc.
  }
  
  // Override in subclasses for kill rewards
  onKill(victim) {
    // Base units get no reward, soldiers might level up, etc.
  }
  
  // Basic rendering
  render(ctx) {
    if (!this.alive) return;
    
    // Save context state
    ctx.save();
    
    // Apply visual effects
    if (this.damageIndicator > 0) {
      // Red flash when damaged
      ctx.shadowBlur = 20 * this.damageIndicator;
      ctx.shadowColor = '#ff0000';
    } else if (this.healingGlow > 0) {
      // Green glow when healing
      ctx.shadowBlur = 15 * this.healingGlow;
      ctx.shadowColor = '#00ff00';
    }
    
    // Draw the unit (basic square)
    ctx.fillStyle = this.color;
    ctx.fillRect(
      this.x - this.size / 2,
      this.y - this.size / 2,
      this.size,
      this.size
    );
    
    // Reset shadow
    ctx.shadowBlur = 0;
    
    // Draw health bar only in debug mode
    if (isDebugMode()) {
      this.renderHealthBar(ctx);
    }
    
    // Restore context state
    ctx.restore();
    
    // Let subclasses add their own rendering
    this.renderSpecific(ctx);
  }
  
  // Override in subclasses for additional rendering
  renderSpecific(ctx) {
    // Subclasses can add weapon indicators, special effects, etc.
  }
  
  // Render health bar
  renderHealthBar(ctx) {
    if (this.health >= this.maxHealth) return; // Don't show if full health
    
    const barWidth = this.size * 2;
    const barHeight = 3;
    const barY = this.y - this.size - 8;
    
    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(
      this.x - barWidth / 2,
      barY,
      barWidth,
      barHeight
    );
    
    // Health bar
    const healthPercent = this.health / this.maxHealth;
    ctx.fillStyle = healthPercent > 0.5 ? '#0f0' : 
                   healthPercent > 0.25 ? '#ff0' : '#f00';
    ctx.fillRect(
      this.x - barWidth / 2,
      barY,
      barWidth * healthPercent,
      barHeight
    );
  }
  
  // Smooth boid-like movement system (original implementation)
  moveTowards(targetX, targetY, speedMultiplier = 1) {
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > 0) {
      let moveSpeed = this.speed * speedMultiplier;
      // Slow down when carrying resources (already handled by speedMultiplier parameter)
      // Don't double-apply the slowdown
      const desiredVx = (dx / distance) * moveSpeed;
      const desiredVy = (dy / distance) * moveSpeed;
      this.easeVelocity(desiredVx, desiredVy);
    }
  }
  
  moveAway(fromX, fromY, speedMultiplier = 1) {
    const dx = this.x - fromX;
    const dy = this.y - fromY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > 0) {
      // Base flee direction
      let fleeX = (dx / distance) * this.speed * speedMultiplier;
      let fleeY = (dy / distance) * this.speed * speedMultiplier;
      this.easeVelocity(fleeX, fleeY);
    } else {
      // Random direction if at same position
      const angle = Math.random() * Math.PI * 2;
      const desiredVx = Math.cos(angle) * this.speed * speedMultiplier;
      const desiredVy = Math.sin(angle) * this.speed * speedMultiplier;
      this.easeVelocity(desiredVx, desiredVy);
    }
  }
  
  // Smart movement with base avoidance (from original)
  moveTowardsWithBaseAvoidance(targetX, targetY, bases = [], speedMultiplier = 1) {
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance === 0) return;

    // Start with direct path to target
    let desiredVx = (dx / distance) * this.speed * speedMultiplier;
    let desiredVy = (dy / distance) * this.speed * speedMultiplier;

    // Calculate avoidance forces from enemy bases
    let avoidanceX = 0;
    let avoidanceY = 0;
    let dangerousBaseCount = 0;

    for (let base of bases) {
      // Skip own team's base and dead bases
      if (!base.alive || base.team.id === this.team.id) continue;

      const baseDistance = this.getDistance(base);
      const dangerZone = base.weaponRange * 1.2; // Avoid getting too close to enemy bases

      if (baseDistance < dangerZone) {
        // Calculate avoidance vector
        const avoidDx = this.x - base.x;
        const avoidDy = this.y - base.y;
        const avoidDistance = Math.sqrt(avoidDx * avoidDx + avoidDy * avoidDy);

        if (avoidDistance > 0) {
          // Stronger avoidance when closer to base
          const avoidanceStrength = (dangerZone - baseDistance) / dangerZone;
          const normalizedAvoidX = (avoidDx / avoidDistance) * avoidanceStrength;
          const normalizedAvoidY = (avoidDy / avoidDistance) * avoidanceStrength;

          avoidanceX += normalizedAvoidX;
          avoidanceY += normalizedAvoidY;
          dangerousBaseCount++;
        }
      }
    }

    // Combine movement toward target with base avoidance
    if (dangerousBaseCount > 0) {
      // Average the avoidance forces
      avoidanceX /= dangerousBaseCount;
      avoidanceY /= dangerousBaseCount;

      // Apply avoidance with high priority
      const avoidanceWeight = 1.5; // Avoidance is more important than direct pathing
      desiredVx = (desiredVx + avoidanceX * avoidanceWeight * this.speed) / (1 + avoidanceWeight);
      desiredVy = (desiredVy + avoidanceY * avoidanceWeight * this.speed) / (1 + avoidanceWeight);
    }

    // Apply speed modifiers
    if ((this.carryingResource && typeof this.carryingResource.x === 'undefined') || 
        (this.metalCargo && this.metalCargo.length > 0)) {
      desiredVx *= GAME_CONFIG.RESOURCE_CARRY_SPEED_MULTIPLIER;
      desiredVy *= GAME_CONFIG.RESOURCE_CARRY_SPEED_MULTIPLIER;
    }

    this.easeVelocity(desiredVx, desiredVy);
  }
  
  // Smooth velocity interpolation for boid-like movement
  easeVelocity(targetVx, targetVy, ease = 0.1) {
    this.vx += (targetVx - this.vx) * ease;
    this.vy += (targetVy - this.vy) * ease;
    
    // Cap velocity to prevent exponential growth
    // Maximum reasonable speed is 2x base speed (for extreme situations)
    const maxSpeed = this.speed * 2.0;
    const currentSpeed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    
    if (currentSpeed > maxSpeed) {
      const scale = maxSpeed / currentSpeed;
      this.vx *= scale;
      this.vy *= scale;
    }
  }
  
  stop() {
    this.vx = 0;
    this.vy = 0;
  }
  
  // Smooth wandering behavior with base avoidance
  wander(speedMultiplier = 1) {
    // Initialize wander angle if not set
    if (this.wanderAngle === undefined) {
      this.wanderAngle = Math.random() * Math.PI * 2;
    }

    // Random walk with smooth direction changes
    this.wanderAngle += (Math.random() - 0.5) * 0.3;

    let desiredVx = Math.cos(this.wanderAngle) * this.speed * speedMultiplier;
    let desiredVy = Math.sin(this.wanderAngle) * this.speed * speedMultiplier;

    // Apply base avoidance (critical for survival!)
    const bases = this.allBases || [];
    for (let base of bases) {
      // Skip own team's base and dead bases
      if (!base.alive || base.team.id === this.team.id) continue;

      const baseDistance = this.getDistance(base);
      const dangerZone = base.weaponRange * 1.5; // Start avoiding well before weapon range

      if (baseDistance < dangerZone) {
        // Calculate avoidance vector
        const avoidDx = this.x - base.x;
        const avoidDy = this.y - base.y;
        const avoidDistance = Math.sqrt(avoidDx * avoidDx + avoidDy * avoidDy);

        if (avoidDistance > 0) {
          // Calculate strength relative to weapon range for stronger avoidance
          // At weapon range edge (180px): strength = 50%
          // At base center: strength clamped to 100%
          const avoidanceStrength = Math.min(1.0, (dangerZone - baseDistance) / base.weaponRange);
          const normalizedAvoidX = avoidDx / avoidDistance;
          const normalizedAvoidY = avoidDy / avoidDistance;

          // Apply very strong avoidance force (3.0x for urgent steering away)
          desiredVx += normalizedAvoidX * this.speed * avoidanceStrength * 3.0;
          desiredVy += normalizedAvoidY * this.speed * avoidanceStrength * 3.0;
        }
      }
    }

    this.easeVelocity(desiredVx, desiredVy);
  }
  
  // Boid-like separation force to prevent clustering
  applySeparationForce(allUnits) {
    let separationX = 0;
    let separationY = 0;
    let nearbyCount = 0;

    for (let unit of allUnits) {
      // Only separate from teammates
      if (unit === this || !unit.alive || unit.health <= 0 || unit.team.id !== this.team.id) continue;

      const distance = this.getDistance(unit);
      
      if (distance < AI_CONFIG.SEPARATION_RADIUS && distance > 0) {
        // Calculate repulsion force (stronger when closer)
        const repulsionStrength = (AI_CONFIG.SEPARATION_RADIUS - distance) / AI_CONFIG.SEPARATION_RADIUS;
        const dx = this.x - unit.x;
        const dy = this.y - unit.y;
        
        // Normalize and apply strength
        const magnitude = Math.sqrt(dx * dx + dy * dy);
        if (magnitude > 0) {
          separationX += (dx / magnitude) * repulsionStrength;
          separationY += (dy / magnitude) * repulsionStrength;
          nearbyCount++;
        }
      }
    }

    if (nearbyCount > 0) {
      // Average the separation forces
      separationX /= nearbyCount;
      separationY /= nearbyCount;
      
      // Apply separation force to velocity
      this.vx += separationX * AI_CONFIG.SEPARATION_FORCE;
      this.vy += separationY * AI_CONFIG.SEPARATION_FORCE;
    }
  }
  
  // Base avoidance force (for soldiers to avoid enemy bases)
  applyBaseAvoidanceForce(bases) {
    if (!bases || bases.length === 0) return;
    
    let avoidanceX = 0;
    let avoidanceY = 0;
    let nearbyBaseCount = 0;

    for (let base of bases) {
      // Skip own team's base and dead bases
      if (!base.alive || base.team.id === this.team.id) continue;
      
      // Exception: don't avoid bases we're actively targeting (for berserkers, etc.)
      if (this.target === base) continue;

      const distance = this.getDistance(base);
      
      if (distance < AI_CONFIG.BASE_AVOIDANCE_RADIUS && distance > 0) {
        // Calculate repulsion force (stronger when closer)
        const repulsionStrength = (AI_CONFIG.BASE_AVOIDANCE_RADIUS - distance) / AI_CONFIG.BASE_AVOIDANCE_RADIUS;
        const dx = this.x - base.x;
        const dy = this.y - base.y;
        
        // Normalize and apply strength
        const magnitude = Math.sqrt(dx * dx + dy * dy);
        if (magnitude > 0) {
          avoidanceX += (dx / magnitude) * repulsionStrength;
          avoidanceY += (dy / magnitude) * repulsionStrength;
          nearbyBaseCount++;
        }
      }
    }

    if (nearbyBaseCount > 0) {
      // Average the avoidance forces
      avoidanceX /= nearbyBaseCount;
      avoidanceY /= nearbyBaseCount;
      
      // Apply avoidance force to velocity
      this.vx += avoidanceX * AI_CONFIG.BASE_AVOIDANCE_FORCE;
      this.vy += avoidanceY * AI_CONFIG.BASE_AVOIDANCE_FORCE;
    }
  }
  
  // Utility methods
  getDistance(other) {
    return getDistance(this, other);
  }
  
  isAlive() {
    return this.alive && this.health > 0;
  }
  
  isEnemy(other) {
    return other && other.team && other.team.id !== this.team.id;
  }
  
  isAlly(other) {
    return other && other.team && other.team.id === this.team.id;
  }
  
  // State management
  setState(newState) {
    if (this.state !== newState) {
      this.previousState = this.state;
      this.state = newState;
      this.onStateChange(this.previousState, newState);
    }
  }
  
  // Override in subclasses to handle state changes
  onStateChange(oldState, newState) {
    // Subclasses can react to state changes
  }
}

// Static ID counter
Unit.nextId = 1;